/**
 * Language Routing Utilities
 * Handles language code in URLs (e.g., /it/professional, /professional/it)
 */

// Supported language codes
export const SUPPORTED_LANGUAGES = [
  "en", "it", "es", "fr", "de", "pt", "ru", "zh", "ja", "ar", "hi", "ko", "nl", "cs"
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number];

/**
 * Extract language code from URL path
 * Supports both /it/professional and /professional/it patterns
 */
export const extractLanguageFromPath = (pathname: string): { 
  language: LanguageCode | null; 
  cleanPath: string;
  position: "prefix" | "suffix" | null;
} => {
  const segments = pathname.split("/").filter(Boolean);
  
  if (segments.length === 0) {
    return { language: null, cleanPath: "/", position: null };
  }

  // Check if first segment is a language code (e.g., /it/professional)
  if (SUPPORTED_LANGUAGES.includes(segments[0] as LanguageCode)) {
    const language = segments[0] as LanguageCode;
    const cleanPath = "/" + segments.slice(1).join("/");
    return { language, cleanPath: cleanPath || "/", position: "prefix" };
  }

  // Check if last segment is a language code (e.g., /professional/it)
  const lastSegment = segments[segments.length - 1];
  if (SUPPORTED_LANGUAGES.includes(lastSegment as LanguageCode)) {
    const language = lastSegment as LanguageCode;
    const cleanPath = "/" + segments.slice(0, -1).join("/");
    return { language, cleanPath: cleanPath || "/", position: "suffix" };
  }

  return { language: null, cleanPath: pathname, position: null };
};

/**
 * Add language code to path
 * Default position is 'prefix' (e.g., /it/professional)
 */
export const addLanguageToPath = (
  pathname: string, 
  language: LanguageCode,
  position: "prefix" | "suffix" = "prefix"
): string => {
  // First remove any existing language code
  const { cleanPath } = extractLanguageFromPath(pathname);
  
  // For root path, return language prefix: /it, /es, etc.
  if (cleanPath === "/") {
    return `/${language}`;
  }

  const segments = cleanPath.split("/").filter(Boolean);

  if (position === "prefix") {
    // Add language as first segment: /it/professional
    return "/" + [language, ...segments].join("/");
  } else {
    // Add language as last segment: /professional/it
    return "/" + [...segments, language].join("/");
  }
};

/**
 * Remove language code from path
 */
export const removeLanguageFromPath = (pathname: string): string => {
  const { cleanPath } = extractLanguageFromPath(pathname);
  return cleanPath;
};

/**
 * Check if a string is a valid language code
 */
export const isValidLanguageCode = (code: string): code is LanguageCode => {
  return SUPPORTED_LANGUAGES.includes(code as LanguageCode);
};

/**
 * Get language code from various sources (URL, localStorage, navigator)
 */
export const detectLanguage = (pathname?: string): LanguageCode => {
  // 1. Try to get from URL
  if (pathname) {
    const { language } = extractLanguageFromPath(pathname);
    if (language) return language;
  }

  // 2. Try to get from localStorage
  const stored = localStorage.getItem("i18nextLng");
  if (stored && isValidLanguageCode(stored)) {
    return stored;
  }

  // 3. Try to get from navigator
  const browserLang = navigator.language.split("-")[0];
  if (isValidLanguageCode(browserLang)) {
    return browserLang;
  }

  // 4. Default to English
  return "en";
};

/**
 * Map language codes to full names
 */
export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  en: "English",
  it: "Italiano",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  ru: "Русский",
  zh: "中文",
  ja: "日本語",
  ar: "العربية",
  hi: "हिन्दी",
  ko: "한국어",
  nl: "Nederlands",
  cs: "Čeština",
};

